import { HostBinding, Injectable } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Router } from '@angular/router';
import {
  Orcamento,
  TableColumnOptions,
  WindowService,
} from '@brunsker/libs/core/ui/table/src';

import {
  BehaviorSubject,
  catchError,
  EMPTY,
  exhaustMap,
  Observable,
  Subscription,
  tap,
} from 'rxjs';
import {
  OrcamentoRequest,
  OrcamentoResponse,
} from '../../../../models/orcamento.models';
import { PaginatorResponse } from '../../../../models/login.models';
import {
  MapperService,
  SelectClientService,
  STATUS_TABLE_HEADEAR_COLOR,
} from '@brunsker/libs/portal-do-vendedor/utils/portal-vendedor-api';
import {
  FilterGroup,
  HistoricoDeVendasFilterGroupService,
  HistoricoDeVendasFilterService,
} from '@brunsker/libs/portal-do-vendedor/ui/historico-de-vendas-filter';
import { NewApiUserService } from '../../../api/new-api-user/new-api-user.service';
import { NewApiOrcamentoService } from '../../../api/new-api-orcamento/new-api-orcamento.service';
import { UtilsToastService } from '@brunsker/libs/core/utils/hot-toast/src';

@Injectable({
  providedIn: 'root',
})
export class HistoricoDeVendasDomainService {
  @HostBinding('style.--table-header') tableHeaderColor!: string;
  columnsOptions: TableColumnOptions[] =
    this.mapperService.createOrcamentoColumnOptions();
  dataDisplay$$ = new BehaviorSubject<Orcamento[]>([]);
  dataDisplay: Observable<Orcamento[]> = this.dataDisplay$$.asObservable();
  private readonly currentPageIndex$$ = new BehaviorSubject<number>(0);
  readonly currentPageIndex$ = this.currentPageIndex$$.asObservable();
  status!: string[];
  hasClient = this.selectClientService.hasClient$$;
  liberaOrca: boolean = false;
  pageSize: number = 30;
  pageSizeOptions: number[] = [30, 50, 100, 200];
  orcamentoTableResponse!: PaginatorResponse<OrcamentoResponse>;
  response!: OrcamentoRequest;
  subscription!: Subscription;
  pageChangeSubscription?: Subscription;

  constructor(
    readonly windowService: WindowService,
    readonly mapperService: MapperService,
    private readonly historicoDeVendasFilterService: HistoricoDeVendasFilterService,
    readonly historicoDeVendasFilter: HistoricoDeVendasFilterGroupService,
    private readonly selectClientService: SelectClientService,
    private newApiUserService: NewApiUserService,
    private readonly newApiOrcamento: NewApiOrcamentoService,
    private readonly toast: UtilsToastService,
    private readonly router: Router,
  ) {}

  readonly openOrcamentoDetails = (orcamento: Orcamento): void => {
    if (orcamento.seqPedido == null) return;

    void this.router.navigate(['/historico-de-vendas/orcamento/orcamento'], {
      queryParams: { id: orcamento.seqPedido },
    });
  };

  start(index: number = 0, length: number = 30): void {
    if (this.subscription) this.subscription.unsubscribe();

    this.currentPageIndex$$.next(index);

    this.validationTypeUser();

    this.subscription = this.historicoDeVendasFilter
      .getFilters()
      .pipe(
        exhaustMap((value) => {
          if (!value) return EMPTY;

          if (typeof value === 'string') {
            value = JSON.parse(value) as FilterGroup;

            const checkboxStatus = JSON.parse(
              localStorage.getItem('checkboxStatus') || '[]',
            );

            value.checkboxData = checkboxStatus;
            length = (+(sessionStorage.getItem('pageIndex') ?? 0) + 1) * 30;

            this.historicoDeVendasFilterService.filterGroup.patchValue(
              value as any,
              {
                emitEvent: false,
              },
            );
          }

          return this.createHistoricoDeVendasResponse(value, { index, length });
        }),
      )
      .subscribe((data: any) => {
        this.dataDisplay$$.next(data);
      });

    this.liberaOrca =
      +(this.newApiUserService.currentUser$$.value?.liberaorca ?? 0) > 0;
  }

  private validationTypeUser() {
    const responseParams = sessionStorage.getItem('responseParams');
    const value = responseParams && (JSON.parse(responseParams) as FilterGroup);
    const userStringify = sessionStorage.getItem('user');
    const user = userStringify && JSON.parse(userStringify);

    if (
      user &&
      user.tipoCargo === 'O' &&
      typeof value === 'object' &&
      value &&
      value.vendedor === ''
    ) {
      value.vendedor = user.idVendedor;
      sessionStorage.setItem('responseParams', JSON.stringify(value));
    }
  }

  private createHistoricoDeVendasResponse(
    value: FilterGroup,
    page: {
      index: number;
      length: number;
    },
  ) {
    this.status = value.checkboxData;

    if (!this.status) {
      this.status = [];
    }

    const arrayToString = this.status
      .map((status) => String(status).trim())
      .filter(
        (status) =>
          status !== '' &&
          Number.isInteger(Number(status)) &&
          Number(status) >= 0,
      )
      .join(',');

    const dataInicial = this.formatData(value.dataInicial);
    const dataFinal = this.formatData(value.dataFinal);

    const pageIndex = page.index * page.length;

    // Caso valores de null passem por esses campos os filtros ficam inultilizáveis.
    if (value.pedido == null) {
      value.pedido = '';
    }
    if (value.orcamento == null) {
      value.orcamento = '';
    }
    if (value.vendedor == null) {
      value.vendedor = '';
    }

    const response: OrcamentoRequest = {
      linhaInicial: pageIndex,
      intervalo: page.length,
      STATUSPED: arrayToString,
      DATAINI: dataInicial,
      DATAFIM: dataFinal,
      SEQ_PEDIDO: value.pedido?.toString().trim(),
      ORCAMENTO_FILIAL: value.orcamento?.toString().trim(),
      ID_FILIAL: value.filial?.toString().trim(),
      ID_CLIENTE: value.cliente?.toString().trim(),
      ID_VENDEDOR: value.vendedor?.toString().trim(),
      pCODUSUR: this.newApiUserService.currentUser$$.value?.idVendedor!,
    };

    this.response = response;

    this.tableHeaderColor = STATUS_TABLE_HEADEAR_COLOR[0];

    return this.mapperService
      .createNewApiOrcamentoTableData(this.response)
      .pipe(
        tap(() => {
          sessionStorage.setItem('responseParams', JSON.stringify(value));
          sessionStorage.setItem('pageIndex', '0');
          this.response.linhaInicial = 0;

          this.mapperService.orcamentoTableResponse.pageIndex =
            this.response.linhaInicial / this.response.intervalo;

          this.orcamentoTableResponse =
            this.mapperService.orcamentoTableResponse;
        }),
      );
  }

  pageChanged(page: PageEvent) {
    this.currentPageIndex$$.next(page.pageIndex);
    const isPageSizeChanged = this.pageSize !== page.pageSize;
    this.pageSize = page.pageSize;

    const targetLinhaInicial =
      page.pageIndex === 0 ? 0 : page.pageIndex * page.pageSize + 1;

    if (
      !isPageSizeChanged &&
      page.pageIndex !== 0 &&
      this.response.linhaInicial >= page.pageIndex * page.pageSize
    ) {
      return;
    }

    this.response.linhaInicial = targetLinhaInicial;
    this.response.intervalo = page.pageSize + 1;

    this.pageChangeSubscription?.unsubscribe();

    this.pageChangeSubscription = this.mapperService
      .createNewApiOrcamentoTableData(this.response)
      .pipe(
        tap(() => {
          sessionStorage.setItem('pageIndex', page.pageIndex.toString());
          this.orcamentoTableResponse =
            this.mapperService.orcamentoTableResponse;
        }),
      )
      .subscribe((data: any) => {
        if (isPageSizeChanged || page.pageIndex === 0) {
          this.dataDisplay$$.next(data);
        } else {
          this.dataDisplay$$.next([...this.dataDisplay$$.value, ...data]);
        }
      });
  }

  formatData(data: string) {
    if (!data) return;
    const date = new Date(data);
    const month =
      date.getMonth() + 1 >= 10
        ? date.getMonth() + 1
        : '0' + (date.getMonth() + 1);
    const day = date.getDate() >= 10 ? date.getDate() : '0' + date.getDate();
    return date.getFullYear() + '/' + month + '/' + day;
  }

  updateAllOneorca() {
    this.newApiOrcamento
      .liberaAllOneOrca({
        pIDVENDEDOR:
          this.newApiUserService.currentUser$$?.value?.idVendedor ?? 0,
      })
      .subscribe((value) => {
        this.toast.showSuccess('Pedidos liberados');
        this.start();
      });
  }
}
